'use client';

import React, { useEffect, useState } from 'react';

import { BookmarkBoldIcon } from '@depop/web-ui-kit/Icons/BookmarkBoldIcon';
import { BookmarkIcon } from '@depop/web-ui-kit/Icons/BookmarkIcon';
import { Modal } from '@depop/web-ui-kit/Modal';

import productInteractionStyles from '../styles.module.css';

import styles from './styles.module.css';

import { DownloadAppModal } from '@/components/DownloadAppModal/DownloadAppModal.tsx';
import { interactionTypes } from '@/modules/activityTracker/constants.ts';
import { useExternalTracking } from '@/modules/externalTracking/useExternalTracking.ts';
import { addProductSave, deleteProductSave } from '@/modules/product/api.ts';
import { PRODUCT_INTERACTION_TYPES } from '@/modules/product/constants.ts';
import { useProductTracking } from '@/modules/product/hooks/useProductTracking.ts';
import { useCurrentUser } from '@/modules/user/useCurrentUser.ts';

type Props = {
  productId: number;
  hasSavedProduct?: boolean;
};

export function ProductSave({ productId, hasSavedProduct }: Props) {
  const [isAuthenticated] = useCurrentUser();
  const [isSaved, setIsSaved] = useState(false);
  const [isOpen, setOpen] = useState(false);
  const { sendProductInteractionActionATEvent } = useProductTracking();
  const {
    gtm: { sendEvent },
  } = useExternalTracking();

  const iconSaveTitle = isSaved ? 'Unsave Product' : 'Save Product';

  function handleUpdateSaveSuccess(isSaved: boolean) {
    sendProductInteractionActionATEvent(
      productId,
      isSaved
        ? PRODUCT_INTERACTION_TYPES.PRODUCT_SAVE
        : PRODUCT_INTERACTION_TYPES.PRODUCT_UNSAVE
    );

    sendEvent({
      category: 'Product Interaction',
      action: isSaved ? 'save product' : 'unsave product',
      label: productId.toString(),
    });
  }

  function updateSaveState() {
    setIsSaved((isSaved) => !isSaved);
  }

  function updateSave() {
    updateSaveState();
    if (isSaved) {
      return deleteProductSave(productId)
        .then(() => handleUpdateSaveSuccess(false))
        .catch(updateSaveState);
    }
    return addProductSave(productId)
      .then(() => handleUpdateSaveSuccess(true))
      .catch(updateSaveState);
  }

  function openModal() {
    setOpen(true);
    sendProductInteractionActionATEvent(
      productId,
      interactionTypes.PRODUCT_SAVE
    );
  }

  useEffect(() => {
    setIsSaved(Boolean(hasSavedProduct));
  }, [hasSavedProduct]);

  if (!isAuthenticated) {
    return (
      <>
        <button
          className={productInteractionStyles.interactionButton}
          onClick={openModal}
          // eslint-disable-next-line no-restricted-syntax
          data-testid="productInteraction__saveButton"
          aria-haspopup="dialog"
        >
          <BookmarkIcon title="Save Product" className={styles.saveIcon} />
        </button>
        <Modal
          isOpen={isOpen}
          setOpen={setOpen}
          content={() => (
            <DownloadAppModal downloadAppTrackingId="product_save" />
          )}
        />
      </>
    );
  }

  return (
    <>
      <button
        aria-label={iconSaveTitle}
        className={productInteractionStyles.interactionButton}
        // eslint-disable-next-line no-restricted-syntax
        data-testid="productInteraction__saveButton"
        onClick={updateSave}
      >
        {isSaved ? (
          <BookmarkBoldIcon
            className={styles.saveIcon}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="productInteraction__saveButton__saveIcon--saved"
          />
        ) : (
          <BookmarkIcon
            className={styles.saveIcon}
            // eslint-disable-next-line no-restricted-syntax
            data-testid="productInteraction__saveButton__saveIcon--unsaved"
          />
        )}
      </button>
    </>
  );
}
